// Évaluations — comparaison des écarts (gap), entretien 1:1
// (créneaux → session de consensus → signatures) et fiche PDF
// imprimable. Ported from the prototype's ev-gap.jsx; state moved
// from the demo store to the API through callbacks.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  EVAL_BILAN_QS,
  evalGapList, evalSnap, evalTemplate, evalTemplateCrits, evalWeighted,
  type EvalCycle, type EvalEmployeeDTO, type EvalNotes, type EvalSlot, type EvaluationDTO, type ObjectiveDTO,
} from '@pointage/shared';
import { cgAddDays, cgFmtLong, entityById } from '../conges/common.js';
import { EvNoteChips, EvRadar, evFmtYmd, evN1, evNum } from './common.js';
import { useUnsavedGuard } from '../../lib/unsaved.js';

const fmtSlotDay = (d: string) => {
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};
const slotEnd = (h: string) => `${String(Number(h.slice(0, 2)) + 1).padStart(2, '0')}:${h.slice(3)}`;

// ─── comparaison (gap) ───────────────────────────────────────
export function EvGapView({ ev, autoMissing, mgrName }: { ev: EvaluationDTO; autoMissing?: string; mgrName: string }) {
  const both = ev.auto.status === 'SUBMITTED' && ev.mgr.status === 'SUBMITTED' && ev.auto.notes && ev.mgr.notes;
  if (!both) {
    return (
      <div className="ev-lock">
        <span className="ic">🔒</span>
        <h4>Comparaison verrouillée</h4>
        <p>
          L'écart auto-évaluation / manager s'affiche dès que <b>les deux parties</b> ont soumis leur évaluation.
          {ev.auto.status !== 'SUBMITTED'
            ? ` ${autoMissing ?? "L'auto-évaluation n'est pas encore soumise."}`
            : " L'évaluation du manager n'est pas encore soumise."}
        </p>
      </div>
    );
  }
  const auto = ev.auto.notes!, mgr = ev.mgr.notes!;
  const crits = evalTemplateCrits(ev.template);
  const gaps = evalGapList(auto, mgr, crits);
  const Wa = evalWeighted(auto, crits), Wm = evalWeighted(mgr, crits);
  const big = gaps.filter((g) => Math.abs(g.d) >= 1).length;
  const delta = Wm.note - Wa.note;
  return (
    <div>
      <div className="ev-gap-head">
        <div className="ev-card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', gap: 26, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <div>
              <div className="ev-note-big" style={{ fontSize: 30, color: 'var(--shf)' }}>{evNum(Wa.note)}<span style={{ fontSize: 13, color: 'var(--ink4)' }}> /5</span></div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Auto-évaluation</div>
            </div>
            <div>
              <div className="ev-note-big" style={{ fontSize: 30, color: 'var(--infoc)' }}>{evNum(Wm.note)}<span style={{ fontSize: 13, color: 'var(--ink4)' }}> /5</span></div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Manager</div>
            </div>
            <div>
              <span className={'ev-delta ' + (delta > 0.05 ? 'pos' : delta < -0.05 ? 'neg' : 'zero')} style={{ fontSize: 14, padding: '6px 11px' }}>
                {delta >= 0 ? '+' : ''}{evNum(delta)}
              </span>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', marginTop: 5 }}>Écart global</div>
            </div>
          </div>
          <div className="ev-gap-note">
            💬 <b>{big}</b> critère{big > 1 ? 's' : ''} avec un écart ≥ 1 point — ce sont les points de départ de
            l'ordre du jour du 1:1. Les écarts les plus forts sont listés en premier.
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.55, background: 'var(--s1)', borderRadius: 10, padding: '10px 13px' }}>
            <b style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink3)' }}>Commentaire du manager ({mgrName})</b><br />
            {ev.mgr.comment || '—'}
          </div>
        </div>
        <div className="ev-card"><EvRadar auto={auto} mgr={mgr} tplCode={ev.template} /></div>
      </div>
      {gaps.map((g, i) => {
        const lo = Math.min(g.a, g.m), hi = Math.max(g.a, g.m);
        const x = (v: number) => ((v - 1) / 4) * 100;
        return (
          <div key={g.crit.id} className="ev-gaprow" style={{ animationDelay: `${i * 0.025}s` }}>
            <div>
              <div className="nm">{g.crit.lbl}</div>
              <div style={{ fontSize: 10, color: 'var(--ink4)', fontWeight: 600 }}>coef ×{evN1(g.crit.coef)}</div>
            </div>
            <div className="track">
              <div className="rail" />
              {g.d !== 0 && <div className="seg" style={{ left: `${x(lo)}%`, width: `${x(hi) - x(lo)}%` }} />}
              <div className="pt m" data-t={`Manager ${evN1(g.m)}`} style={{ left: `calc(${x(g.m)}% - 7px)` }} />
              <div className="pt a" data-t={`Auto ${evN1(g.a)}`} style={{ left: `calc(${x(g.a)}% - 7px)` }} />
            </div>
            <span className={'ev-delta ' + (g.d > 0 ? 'pos' : g.d < 0 ? 'neg' : 'zero')}>{g.d > 0 ? '+' : ''}{evN1(g.d)}</span>
          </div>
        );
      })}
      <div className="ev-legend" style={{ marginTop: 12 }}>
        <span><i style={{ background: 'var(--shf)' }} />Auto-évaluation</span>
        <span><i style={{ background: 'var(--info)' }} />Manager</span>
        <span style={{ color: 'var(--ink4)', fontWeight: 600 }}>Échelle 1 → 5 · survolez les points</span>
      </div>
    </div>
  );
}

// ─── 1:1 : créneaux, session de consensus, signatures ────────
export interface EvOtoActions {
  propose?: (slots: EvalSlot[]) => void;
  choose?: (slot: EvalSlot) => void;
  close?: (notes: EvalNotes, cr: string) => void;
  signMgr?: () => void;
  signEmp?: () => void;
  print?: () => void;
}

export function EvOtoView({ emp, ev, cycle, persona, mgrName, actions, busy }: {
  emp: EvalEmployeeDTO;
  ev: EvaluationDTO;
  cycle: EvalCycle;
  persona: 'mgr' | 'emp';
  mgrName: string;
  actions: EvOtoActions;
  busy?: boolean;
}) {
  const [session, setSession] = useState(false);
  const [slots, setSlots] = useState<EvalSlot[]>(() => [
    { d: cycle.otoStart, h: '10:00' },
    { d: cgAddDays(cycle.otoStart, 2), h: '14:30' },
    { d: cgAddDays(cycle.otoStart, 4), h: '09:30' },
  ]);
  const tplCrits = evalTemplateCrits(ev.template);
  const [cons, setCons] = useState<EvalNotes>(() => {
    const o: EvalNotes = {};
    if (ev.auto.notes && ev.mgr.notes) {
      // Start the consensus from the sheet's final notes when they were
      // imported, otherwise from the snapped auto/mgr midpoint.
      for (const c of tplCrits) {
        o[c.id] = ev.final.notes?.[c.id] ?? evalSnap(((ev.auto.notes[c.id] ?? 3) + (ev.mgr.notes[c.id] ?? 3)) / 2);
      }
    }
    return o;
  });
  const [cr, setCr] = useState('');
  const both = ev.auto.status === 'SUBMITTED' && ev.mgr.status === 'SUBMITTED';
  const isMgr = persona === 'mgr';
  // Un entretien de clôture ouvert (consensus + compte rendu) est un
  // brouillon : on ne le perd pas sur un changement de société ou de page.
  useUnsavedGuard({
    id: `eval-cloture-${emp.id}`,
    dirty: session && !ev.final.done,
    label: 'Évaluations · entretien de clôture',
    summary: emp.name,
    discard: () => { setSession(false); setCr(''); },
  });

  // Clôturée & signée.
  if (ev.final.done) {
    return (
      <div>
        <div className="ev-oto-banner">
          <div className="cal"><b>✓</b><span>signée</span></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800 }}>Évaluation clôturée — consensus signé par les deux parties</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 2 }}>
              Note finale <b className="gm-mono" style={{ color: 'var(--shf)' }}>{evNum(evalWeighted(ev.final.notes, tplCrits).note)}/5</b>
              {' '}· 1:1 du {ev.oto.chosen ? `${fmtSlotDay(ev.oto.chosen.d)} à ${ev.oto.chosen.h}` : '—'}
            </div>
          </div>
          {actions.print && <button className="cg-ghost" onClick={actions.print}>⤓ Exporter la fiche (PDF)</button>}
        </div>
        <div className="ev-card" style={{ padding: '16px 20px', marginBottom: 13 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink3)', marginBottom: 7 }}>Compte-rendu de l'entretien</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--ink2)' }}>{ev.final.cr}</div>
        </div>
        <div className="ev-signs">
          <div className="ev-sign ok"><div className="who">{emp.name}</div><div className="rl">Employé</div><div className="stamp">✓ Signé le {evFmtYmd(ev.final.signedEmpAt)}</div></div>
          <div className="ev-sign ok"><div className="who">{mgrName}</div><div className="rl">Manager</div><div className="stamp">✓ Signé le {evFmtYmd(ev.final.signedMgrAt)}</div></div>
        </div>
      </div>
    );
  }

  // Both not submitted yet → locked.
  if (!both) {
    const slotsReady = ev.oto.stage === 'PROPOSED' && !isMgr;
    return (
      <div>
        <div className="ev-lock">
          <span className="ic">🗓</span>
          <h4>Entretien 1:1 — en attente des évaluations</h4>
          <p>
            {slotsReady
              ? `${mgrName} a déjà proposé ${ev.oto.slots.length} créneaux — mais le choix ne s'ouvre qu'une fois les deux évaluations soumises.`
              : 'Le 1:1 se planifie une fois les deux évaluations soumises.'}
            {' '}Il sert à discuter des écarts et à fixer la note finale en consensus. Fenêtre : <b>{cycle.otoWin}</b>.
          </p>
        </div>
        {slotsReady && (
          <div className="ev-slots" style={{ opacity: 0.55, pointerEvents: 'none', maxWidth: 720, margin: '0 auto' }}>
            {ev.oto.slots.map((s, i) => (
              <button key={i} className="ev-slot" disabled>
                <div className="d">{fmtSlotDay(s.d)}</div>
                <div className="h">{s.h} → {slotEnd(s.h)}</div>
              </button>
            ))}
          </div>
        )}
        {isMgr && ev.oto.stage === 'NONE' && actions.propose && (
          <div>
            <div className="ev-gap-note">Vous pouvez déjà proposer les créneaux — {emp.name.split(' ').at(-1)} les verra, et pourra choisir dès la soumission des deux évaluations.</div>
            <SlotEditor slots={slots} setSlots={setSlots} cycle={cycle} onSend={() => actions.propose!(slots)} busy={busy} empName={emp.name} />
          </div>
        )}
      </div>
    );
  }

  // Session de consensus en cours.
  if (session) {
    const gaps = evalGapList(ev.auto.notes!, ev.mgr.notes!, tplCrits).filter((g) => Math.abs(g.d) >= 1);
    const Wf = evalWeighted(cons, tplCrits);
    return (
      <div>
        <div className="ev-oto-banner">
          <div className="cal"><b>▶</b><span>en cours</span></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800 }}>Session 1:1 — recherche de consensus</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 2 }}>Parcourez l'ordre du jour, ajustez la note finale critère par critère, rédigez le compte-rendu puis signez.</div>
          </div>
          <button className="cg-ghost" onClick={() => setSession(false)}>Quitter</button>
        </div>
        <div className="ev-card" style={{ padding: '15px 20px', marginBottom: 14 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink3)', marginBottom: 4 }}>Ordre du jour généré — écarts les plus forts d'abord</div>
          <div className="ev-agenda">
            {gaps.length
              ? gaps.map((g) => (
                <div key={g.crit.id} className="ev-agenda-row">
                  <p><b>{g.crit.lbl}</b> <span>— auto {evN1(g.a)} vs manager {evN1(g.m)} (écart {g.d > 0 ? '+' : ''}{evN1(g.d)})</span></p>
                </div>
              ))
              : <div className="ev-agenda-row"><p>Aucun écart ≥ 1 point — parcourir le bilan qualitatif et les objectifs N+1.</p></div>}
            <div className="ev-agenda-row"><p><b>Bilan qualitatif</b> <span>— réalisations, déceptions, difficultés</span></p></div>
            <div className="ev-agenda-row"><p><b>Objectifs {cycle.year + 1}</b> <span>— co-construction et validation</span></p></div>
          </div>
        </div>
        <div className="ev-blockh">
          <span className="n">✦</span><h4>Note finale en consensus</h4>
          <span className="avg">finale <b>{evNum(Wf.note)}</b>/5</span>
        </div>
        {tplCrits.map((c) => (
          <div key={c.id} className="ev-consrow">
            <div><div style={{ fontSize: 12.5, fontWeight: 700 }}>{c.lbl}</div></div>
            <div className="duo">
              <b className="da">A {evN1(ev.auto.notes![c.id])}</b>
              <b className="dm">M {evN1(ev.mgr.notes![c.id])}</b>
            </div>
            <EvNoteChips compact value={cons[c.id]} onPick={(v) => setCons((s) => ({ ...s, [c.id]: v == null ? s[c.id]! : v }))} />
          </div>
        ))}
        <div className="ev-bilan-q" style={{ marginTop: 18 }}>
          <label>Compte-rendu de l'entretien</label>
          <textarea className="ev-ta" style={{ minHeight: 96 }} value={cr}
            placeholder="Points d'accord, plan de développement, décisions prises…" onChange={(e) => setCr(e.target.value)} />
        </div>
        <div className="ev-formfoot">
          <div><div className="gn">{evNum(Wf.note)}<i> /5</i></div><div className="lb">Note finale consensus</div></div>
          <div className="lb" style={{ flex: 1 }}>La clôture envoie la fiche aux deux parties pour signature.</div>
          <button className="gm-attn-go" disabled={!cr.trim() || busy}
            onClick={() => { actions.close?.(cons, cr); setSession(false); }}>
            {cr.trim() ? 'Clôturer & passer aux signatures' : 'Rédigez le compte-rendu'}
          </button>
        </div>
      </div>
    );
  }

  // Consensus établi → signatures.
  if (ev.final.cr) {
    return (
      <div>
        <div className="ev-oto-banner">
          <div className="cal"><b>✍</b><span>à signer</span></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800 }}>Consensus établi — signatures en attente</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 2 }}>
              Note finale <b className="gm-mono" style={{ color: 'var(--shf)' }}>{evNum(evalWeighted(ev.final.notes, tplCrits).note)}/5</b> — chaque partie signe depuis son espace.
            </div>
          </div>
        </div>
        <div className="ev-card" style={{ padding: '16px 20px', marginBottom: 13 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink3)', marginBottom: 7 }}>Compte-rendu</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--ink2)' }}>{ev.final.cr}</div>
        </div>
        <div className="ev-signs">
          <div className={'ev-sign' + (ev.final.signedEmpAt ? ' ok' : '')}>
            <div className="who">{emp.name}</div><div className="rl">Employé</div>
            {ev.final.signedEmpAt
              ? <div className="stamp">✓ Signé le {evFmtYmd(ev.final.signedEmpAt)}</div>
              : (persona === 'emp' && actions.signEmp
                ? <button className="gm-attn-go" disabled={busy} onClick={actions.signEmp}>✍ Signer la fiche</button>
                : <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 10, fontWeight: 600 }}>En attente de signature…</div>)}
          </div>
          <div className={'ev-sign' + (ev.final.signedMgrAt ? ' ok' : '')}>
            <div className="who">{mgrName}</div><div className="rl">Manager</div>
            {ev.final.signedMgrAt
              ? <div className="stamp">✓ Signé le {evFmtYmd(ev.final.signedMgrAt)}</div>
              : (isMgr && actions.signMgr
                ? <button className="gm-attn-go" disabled={busy} onClick={actions.signMgr}>✍ Signer la fiche</button>
                : <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 10, fontWeight: 600 }}>En attente de signature…</div>)}
          </div>
        </div>
      </div>
    );
  }

  // 1:1 planifié → banner + démarrer la session (mgr).
  if (ev.oto.chosen) {
    const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
    return (
      <div>
        <div className="ev-oto-banner">
          <div className="cal"><b>{ev.oto.chosen.d.slice(8)}</b><span>{months[Number(ev.oto.chosen.d.slice(5, 7)) - 1]}</span></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800 }}>1:1 confirmé — {fmtSlotDay(ev.oto.chosen.d)} à {ev.oto.chosen.h}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 2 }}>Créneau confirmé par les deux parties · 60 min</div>
          </div>
          {isMgr && actions.close && <button className="gm-attn-go" onClick={() => setSession(true)}>▶ Démarrer la session</button>}
        </div>
        <div className="ev-card" style={{ padding: '15px 20px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink3)', marginBottom: 4 }}>Ordre du jour proposé (généré depuis les écarts)</div>
          <div className="ev-agenda">
            {evalGapList(ev.auto.notes!, ev.mgr.notes!, tplCrits).filter((g) => Math.abs(g.d) >= 1).slice(0, 5).map((g) => (
              <div key={g.crit.id} className="ev-agenda-row">
                <p><b>{g.crit.lbl}</b> <span>— auto {evN1(g.a)} vs manager {evN1(g.m)}</span></p>
              </div>
            ))}
            <div className="ev-agenda-row"><p><b>Bilan qualitatif & objectifs N+1</b></p></div>
          </div>
        </div>
      </div>
    );
  }

  // Créneaux proposés → l'employé choisit.
  if (ev.oto.stage === 'PROPOSED') {
    return (
      <div>
        <div className="ev-oto-banner">
          <div className="cal"><b>?</b><span>à caler</span></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800 }}>
              {isMgr ? `Créneaux envoyés — en attente du choix de ${emp.name}` : 'Votre manager propose 3 créneaux — choisissez'}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 2 }}>Fenêtre d'entretiens : {cycle.otoWin} · durée 60 min</div>
          </div>
        </div>
        <div className="ev-slots">
          {ev.oto.slots.map((s, i) => (
            <button key={i} className="ev-slot" disabled={isMgr || busy} style={isMgr ? { cursor: 'default', opacity: 0.75 } : undefined}
              onClick={() => actions.choose?.(s)}>
              <div className="d">{fmtSlotDay(s.d)}</div>
              <div className="h">{s.h} → {slotEnd(s.h)}</div>
            </button>
          ))}
        </div>
        {!isMgr && (
          <div className="ev-gap-note">
            Un créneau ne convient pas ? Contactez directement {mgrName} — de nouveaux créneaux pourront être proposés.
          </div>
        )}
      </div>
    );
  }

  // Rien encore → le manager propose.
  return (
    <div>
      <div className="ev-oto-banner">
        <div className="cal"><b>+</b><span>planifier</span></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800 }}>{isMgr ? 'Proposez des créneaux pour le 1:1' : 'En attente des créneaux du manager'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 2 }}>Les deux évaluations sont soumises — place à la discussion. Fenêtre : {cycle.otoWin}.</div>
        </div>
      </div>
      {isMgr && actions.propose && (
        <SlotEditor slots={slots} setSlots={setSlots} cycle={cycle} onSend={() => actions.propose!(slots)} busy={busy} empName={emp.name} />
      )}
    </div>
  );
}

function SlotEditor({ slots, setSlots, cycle, onSend, busy, empName }: {
  slots: EvalSlot[];
  setSlots: (fn: (s: EvalSlot[]) => EvalSlot[]) => void;
  cycle: EvalCycle;
  onSend: () => void;
  busy?: boolean;
  empName: string;
}) {
  return (
    <div>
      <div className="ev-slots">
        {slots.map((s, i) => (
          <div key={i} className="ev-slot" style={{ cursor: 'default' }}>
            <div className="ev-f2" style={{ gap: 8 }}>
              <div className="ev-field" style={{ margin: 0 }}>
                <label>Date</label>
                <input type="date" value={s.d} min={cycle.otoStart} max={cycle.otoEnd}
                  onChange={(e) => setSlots((ss) => ss.map((x, j) => (j === i ? { ...x, d: e.target.value } : x)))} />
              </div>
              <div className="ev-field" style={{ margin: 0 }}>
                <label>Heure</label>
                <input type="time" value={s.h}
                  onChange={(e) => setSlots((ss) => ss.map((x, j) => (j === i ? { ...x, h: e.target.value } : x)))} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button className="gm-attn-go" disabled={busy} onClick={onSend}>Envoyer les 3 créneaux à {empName.split(' ').at(-1)}</button>
    </div>
  );
}

// ─── fiche imprimable (portal sous <body>) ───────────────────
export function EvFiche({ emp, ev, objs, mgrName, cycle }: {
  emp: EvalEmployeeDTO & { hiredAt?: string | null };
  ev: EvaluationDTO;
  objs: ObjectiveDTO[];
  mgrName: string;
  cycle: EvalCycle;
}) {
  const tpl = evalTemplate(ev.template);
  const crits = evalTemplateCrits(ev.template);
  const Wa = evalWeighted(ev.auto.notes, crits), Wm = evalWeighted(ev.mgr.notes, crits), Wf = evalWeighted(ev.final.notes, crits);
  const ent = entityById(emp.entityId);
  const bilan = ev.auto.bilan ?? [];
  return createPortal(
    <div className="ev-fiche">
      <div className="fh">
        <div>
          <h1>ÉVALUATION ANNUELLE — {ent?.name?.toUpperCase() ?? 'SHIFTIN'}</h1>
          <div style={{ fontSize: '11px', marginTop: '4px' }}>{cycle.lbl} · Période évaluée : {cycle.periode}</div>
        </div>
        <svg width="46" height="46" viewBox="0 0 64 64">
          <rect width="64" height="64" rx="16" fill="#7037ef" />
          <rect x="14" y="18" width="30" height="7" rx="3.5" fill="#fff" />
          <rect x="24" y="28.5" width="26" height="7" rx="3.5" fill="#fff" opacity=".62" />
          <rect x="14" y="39" width="20" height="7" rx="3.5" fill="#fff" />
        </svg>
      </div>
      <table><tbody>
        <tr>
          <td><b>Nom & prénom :</b> {emp.name}</td>
          <td><b>Poste :</b> {emp.department ?? '—'}</td>
          <td><b>Entité :</b> {ent?.name ?? emp.entityId}</td>
        </tr>
        <tr>
          <td><b>Date d'entrée :</b> {evFmtYmd(emp.hiredAt)}</td>
          <td><b>Évaluateur :</b> {mgrName}</td>
          <td><b>Clôture :</b> {evFmtYmd(ev.final.signedMgrAt)}</td>
        </tr>
      </tbody></table>
      <table>
        <thead><tr><th style={{ width: '34%' }}>Critère</th><th>Coef.</th><th>Auto-éval</th><th>Manager</th><th>Éval finale</th><th>Pondérée</th></tr></thead>
        <tbody>
          {tpl.blocks.map((b) => [
            <tr key={b.id} className="blk"><td colSpan={6}>{b.lbl}</td></tr>,
            ...b.crits.map((c) => (
              <tr key={c.id}>
                <td>{c.lbl}</td>
                <td className="num">{evN1(c.coef)}</td>
                <td className="num">{evN1(ev.auto.notes?.[c.id])}</td>
                <td className="num">{evN1(ev.mgr.notes?.[c.id])}</td>
                <td className="num"><b>{evN1(ev.final.notes?.[c.id])}</b></td>
                <td className="num">{ev.final.notes?.[c.id] != null ? evNum(ev.final.notes[c.id]! * c.coef) : '—'}</td>
              </tr>
            )),
          ])}
          <tr className="blk">
            <td>NOTE GLOBALE /5</td><td />
            <td className="num">{evNum(Wa.note)}</td>
            <td className="num">{evNum(Wm.note)}</td>
            <td className="num"><b>{evNum(Wf.note)}</b></td>
            <td />
          </tr>
        </tbody>
      </table>
      <h3>Bilan qualitatif</h3>
      {EVAL_BILAN_QS.map((q, i) => (
        <div key={i}><div className="q">{i + 1}. {q}</div><div className="a">{bilan[i] || '—'}</div></div>
      ))}
      <div className="q">Compte-rendu de l'entretien 1:1</div>
      <div className="a">{ev.final.cr || '—'}</div>
      <h3>Objectifs pour l'année prochaine</h3>
      <table>
        <thead><tr><th>Catégorie</th><th>Objectif</th><th>KPI</th><th>Deadline</th></tr></thead>
        <tbody>
          {objs.filter((o) => o.status === 'VALIDE').map((o) => (
            <tr key={o.id}><td>{o.cat}</td><td>{o.title}</td><td>{o.kpi}</td><td>{o.deadline}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="sigrow">
        <div className="sigbox"><b>Signature employé</b><br />{emp.name}{ev.final.signedEmpAt ? ` — signé électroniquement le ${evFmtYmd(ev.final.signedEmpAt)}` : ''}</div>
        <div className="sigbox"><b>Signature manager</b><br />{mgrName}{ev.final.signedMgrAt ? ` — signé électroniquement le ${evFmtYmd(ev.final.signedMgrAt)}` : ''}</div>
      </div>
    </div>,
    document.body,
  );
}
